import React, { Component } from "react";

import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ButtonBase from "@material-ui/core/ButtonBase";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Popper from "@material-ui/core/Popper";
import Paper from "@material-ui/core/Paper";
import Grow from "@material-ui/core/Grow";

import FilterIcon from "@material-ui/icons/FilterList";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";

import styled, { css } from "styled-components";

import { filterList, FILTER_TYPES } from "../../../utils/filter";
import DateFilterDialog from "./components/DateFilterDialog";

const Container = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 40px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }
`;

const SelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  border-right: 1px solid var(--color-border);
  background: var(--color-surface-subtle);
  min-width: 160px;
`;

const FilterSelector = styled(ButtonBase)`
  && {
    height: 100%;
    width: 100%;
    justify-content: flex-start;
    padding: 0 var(--space-3);
    font-family: var(--font-sans);
  }
`;

const SelectorContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--space-2);
`;

const SelectorLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const chevronStyles = css`
  && {
    color: var(--color-text-tertiary);
    font-size: 18px;
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  ${chevronStyles}
`;

const ExpandMoreIcon = styled(ExpandMore)`
  ${chevronStyles}
`;

const TextBoxWrapper = styled.div<{ disabled: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 var(--space-3);
  background: ${(p) =>
    p.disabled ? "var(--color-surface-subtle)" : "var(--color-surface)"};
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);

  &::placeholder {
    color: var(--color-text-tertiary);
    text-transform: none;
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-tertiary);
  }
`;

const MenuPaper = styled(Paper)`
  && {
    background: var(--color-surface) !important;
    color: var(--color-text) !important;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    margin-top: 4px;
    min-width: 240px;
    overflow: hidden;
  }

  .MuiMenuItem-root {
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    padding: var(--space-2) var(--space-3);
  }

  .MuiMenuItem-root.Mui-selected {
    background: var(--color-accent-subtle);
    color: var(--color-accent-text);
  }

  .MuiMenuItem-root:hover {
    background: var(--color-surface-subtle);
  }

  .MuiListItemIcon-root {
    color: var(--color-text-tertiary);
    min-width: 28px;
  }

  .MuiListItemText-primary {
    font-family: var(--font-sans);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

type Props = {
  shouldFilterAfterSelectFilter: boolean;
  filterConfig: Array<any>;
  onFilterData: Function;
  dataset: Array<any>;
};

type State = {
  isChooseDateDialogOpen: boolean;
  filterButtonLabel: string;
  filterSelected: string;
  filterValue: string;
  isFilterOpen: boolean;
  isFunctionalFilter: boolean;
};

class Filter extends Component<Props, State> {
  state = {
    filterButtonLabel: "Add filter",
    isChooseDateDialogOpen: false,
    filterSelected: "ADD_FILTER",
    isFunctionalFilter: false,
    isFilterOpen: false,
    filterValue: "",
  };

  onToggleFilter = (): void => {
    const { isFilterOpen } = this.state;
    this.setState({ isFilterOpen: !isFilterOpen });
  };

  onToggleDateDialog = (): void => {
    const { isChooseDateDialogOpen } = this.state;
    this.setState({ isChooseDateDialogOpen: !isChooseDateDialogOpen });
  };

  onFilterAll = (): void => {
    const { onFilterData, dataset } = this.props;
    onFilterData(dataset);
  };

  onResetFilter = (): void => {
    const { onFilterData } = this.props;
    onFilterData([]);
  };

  onFilter = (filterValue: string): Array<Object> => {
    const dataFiltered = this.handleFilterByType(filterValue);
    return dataFiltered;
  };

  onSelectFilter = (filterItem: any): void => {
    const { shouldFilterAfterSelectFilter } = this.props;
    const { filterSelected, filterValue } = this.state;
    const { filterTitle, dataField } = filterItem;

    if (filterItem.type === FILTER_TYPES.FUNCTIONAL) {
      this.handleFunctionalFilter(filterItem);
      return;
    }

    if (filterItem.type === FILTER_TYPES.DATE.ID) {
      this.setState({
        isChooseDateDialogOpen: true,
        isFilterOpen: false,
      });
    }

    const isDifferentFilter = filterSelected !== dataField;
    const shouldResetItemsFiltered =
      !shouldFilterAfterSelectFilter &&
      isDifferentFilter &&
      dataField !== "all";
    const filterButtonLabel =
      dataField === "all" ? "Show all" : `${filterTitle}`;

    if (isDifferentFilter) {
      this.onFilterAll();
    }

    if (shouldResetItemsFiltered) {
      this.onResetFilter();
    }

    this.setState({
      filterValue: filterSelected === dataField ? filterValue : "",
      isFunctionalFilter: false,
      filterSelected: dataField,
      isFilterOpen: false,
      filterButtonLabel,
    });
  };

  onChangeFilterValue = (filterValue: string): void => {
    const { shouldFilterAfterSelectFilter, onFilterData } = this.props;

    const filterResult = this.onFilter(filterValue);
    const shouldResetItemsFiltered =
      !shouldFilterAfterSelectFilter && !filterValue;
    const properCallback = shouldResetItemsFiltered
      ? this.onResetFilter
      : onFilterData;

    this.setState({ filterValue }, () => properCallback(filterResult));
  };

  getTextInputPlaceholder = (): string => {
    const { filterSelected } = this.state;

    const isAllFilterSelected = filterSelected === "all";
    if (isAllFilterSelected) return "All records";

    const isInitialPlaceholder = filterSelected === "ADD_FILTER";
    if (isInitialPlaceholder) return "Choose a filter to search";

    const filterSelectedConfig = this.getFilterSelectedConfig();
    const { placeholder } = filterSelectedConfig;

    return placeholder;
  };

  getFilterSelectedConfig = () => {
    const { filterSelected } = this.state;
    const { filterConfig } = this.props;

    const filterSelectedIndex = filterConfig.findIndex(
      (filter) => filter.dataField === filterSelected
    );

    return filterConfig[filterSelectedIndex];
  };

  handleFilterByType = (filterValue: string) => {
    const { filterSelected } = this.state;

    const filterSelectedConfig = this.getFilterSelectedConfig();
    const { type } = filterSelectedConfig;

    let dataFiltered = [];

    if (type === FILTER_TYPES.NUMERIC) {
      dataFiltered = this.handleNumericFilter(
        filterSelected,
        filterSelectedConfig,
        filterValue
      );
    }

    if (type === FILTER_TYPES.TEXT) {
      dataFiltered = this.handleTextFilter(filterSelected, filterValue);
    }

    if (type === FILTER_TYPES.DATE.ID) {
      dataFiltered = this.handleDateFilter(filterSelected, filterValue);
    }

    return dataFiltered;
  };

  handleFunctionalFilter = (filterConfig: any): void => {
    const { filterTitle, filterLabel, dataField, behavior } = filterConfig;
    const { onFilterData, dataset } = this.props;

    const filterParams = {
      type: FILTER_TYPES.FUNCTIONAL,
      behavior,
      dataset,
    };

    const data = filterList(filterParams);

    this.setState(
      {
        filterButtonLabel: filterTitle,
        filterSelected: dataField,
        filterValue: filterLabel,
        isFunctionalFilter: true,
        isFilterOpen: false,
      },
      () => onFilterData(data)
    );
  };

  handleDateFilter = (
    filterSelected: Object,
    filterValue: string
  ): Array<Object> => {
    const { dataset } = this.props;

    return filterList({
      type: FILTER_TYPES.DATE.ID,
      value: filterValue,
      filterSelected,
      dataset,
    });
  };

  handleNumericFilter = (
    filterSelected: string,
    filterConfig: any,
    filterValue: string
  ): Array<Object> => {
    const { dataset } = this.props;

    return filterList({
      operator: filterConfig.operator,
      type: FILTER_TYPES.NUMERIC,
      filter: filterSelected,
      value: filterValue,
      dataset,
    });
  };

  handleTextFilter = (
    filterSelected: string,
    filterValue: string
  ): Array<Object> => {
    const { dataset } = this.props;

    return filterList({
      type: FILTER_TYPES.TEXT,
      filter: filterSelected,
      value: filterValue,
      dataset,
    });
  };

  renderMenuItems = () => {
    const { filterSelected } = this.state;
    const { filterConfig } = this.props;

    const extraFilter = [
      { filterTitle: "Show all", dataField: "all" },
      { filterTitle: "FILTER BY", dataField: "$" },
    ];

    const data = [...extraFilter, ...filterConfig];

    const HeaderItem = (
      <MenuItem key="$" disabled>
        <ListItemIcon>
          <FilterIcon style={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText primary="FILTER BY" />
      </MenuItem>
    );

    return (
      <MenuList>
        {data.map((item) =>
          item.dataField === "$" ? (
            HeaderItem
          ) : (
            <MenuItem
              selected={filterSelected === item.dataField}
              onClick={() => this.onSelectFilter(item)}
              key={item.filterTitle}
            >
              {item.filterTitle}
            </MenuItem>
          )
        )}
      </MenuList>
    );
  };

  renderSelector = (): React.ReactNode => {
    const { filterButtonLabel, isFilterOpen } = this.state;

    return (
      <SelectorWrapper>
        <FilterSelector
          aria-owns={isFilterOpen ? "menu-list-grow" : null}
          onClick={() => this.onToggleFilter()}
          buttonRef={(node) => {
            this.anchorEl = node;
          }}
          aria-haspopup="true"
        >
          <SelectorContent>
            <SelectorLabel>{filterButtonLabel}</SelectorLabel>
            {isFilterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </SelectorContent>
        </FilterSelector>
      </SelectorWrapper>
    );
  };

  renderFilterMenu = (): React.ReactNode => {
    const { isFilterOpen } = this.state;

    return (
      <Popper
        anchorEl={this.anchorEl}
        open={isFilterOpen}
        placement="bottom-start"
        disablePortal
        transition
        style={{ zIndex: 100 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <MenuPaper>
              <ClickAwayListener onClickAway={this.onToggleFilter}>
                {this.renderMenuItems()}
              </ClickAwayListener>
            </MenuPaper>
          </Grow>
        )}
      </Popper>
    );
  };

  renderFilterInput = (): React.ReactNode => {
    const { isFunctionalFilter, filterSelected, filterValue } = this.state;

    const isFilterSelectedDateType =
      filterSelected === FILTER_TYPES.DATE.WHEN.BEFORE ||
      filterSelected === FILTER_TYPES.DATE.WHEN.AFTER ||
      filterSelected === FILTER_TYPES.DATE.WHEN.SAME;

    const isInputDisabled =
      isFilterSelectedDateType ||
      filterSelected === "ADD_FILTER" ||
      filterSelected === "all" ||
      isFunctionalFilter;

    return (
      <TextBoxWrapper disabled={isInputDisabled}>
        <SearchInput
          onChange={(event) => this.onChangeFilterValue(event.target.value)}
          placeholder={this.getTextInputPlaceholder()}
          disabled={isInputDisabled}
          value={filterValue || ""}
        />
      </TextBoxWrapper>
    );
  };

  renderDateDialog = (): React.ReactNode => {
    const { isChooseDateDialogOpen } = this.state;
    const { onFilterData } = this.props;

    const onChooseDate = (date: string): void => {
      this.setState(
        {
          isChooseDateDialogOpen: false,
          filterValue: date,
        },
        () => {
          const dataset = this.onFilter(date);
          onFilterData(dataset);
        }
      );
    };

    return (
      <DateFilterDialog
        isChooseDateDialogOpen={isChooseDateDialogOpen}
        onToggleDateDialog={this.onToggleDateDialog}
        onChooseDate={(date) => onChooseDate(date)}
      />
    );
  };

  anchorEl: any;

  render() {
    return (
      <div style={{ position: "relative", width: "100%" }}>
        <Container>
          {this.renderSelector()}
          {this.renderFilterInput()}
        </Container>
        {this.renderFilterMenu()}
        {this.renderDateDialog()}
      </div>
    );
  }
}

export default Filter;
